import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, set, remove } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { Container, Row, Col, Card, Button, Form, Alert, Modal, Nav, Tab } from 'react-bootstrap';
import { Plus, Edit, Trash2, Upload, X, Image } from 'lucide-react';
import './BuildingCheckTemplatesEditor.css';
import { 
    BuildingCheckTemplate, 
    BuildingCheckTemplateFormData, 
    INTERVAL_UNITS, 
    NEXT_DUE_DATE_OPTIONS,
    ConditionAssessmentSettings,
    CONDITION_ASSESSMENT_CATEGORIES,
    DEFAULT_CONDITION_ASSESSMENT_SETTINGS
} from './types';
import { PhotoModel } from '../../types/protocols';
import { Helmet } from 'react-helmet-async';

const BuildingCheckTemplatesEditor: React.FC = () => {
    const [activeTab, setActiveTab] = useState<string>('templates');
    const [templates, setTemplates] = useState<BuildingCheckTemplate[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [editingTemplate, setEditingTemplate] = useState<BuildingCheckTemplate | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [templateToDelete, setTemplateToDelete] = useState<BuildingCheckTemplate | null>(null);
    const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
    const [showValidation, setShowValidation] = useState(false);
    const [modalAlert, setModalAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

    // Condition Assessment Settings State
    const [conditionAssessment, setConditionAssessment] = useState<ConditionAssessmentSettings>(DEFAULT_CONDITION_ASSESSMENT_SETTINGS);

    const [formData, setFormData] = useState<BuildingCheckTemplateFormData>({
        name: '',
        description: '',
        estimatedTime: 0,
        takesMeasurement: {
            enabled: false,
            metric: 'mm',
            hasMinValue: false,
            hasMaxValue: false,
            minValue: 0,
            maxValue: 100
        },
        instructionalAttachments: {},
        checklist: [''],
        interval: {
            value: 1,
            unit: 'months'
        },
        nextDueDateCalculation: 'scheduled',
        photoRequired: false,
        estimatedCost: 0,
        advanceNotice: 30
    });

    const db = getDatabase();
    const storage = getStorage();
    const auth = getAuth();

    // Load templates from Firebase
    const loadTemplates = async (): Promise<void> => {
        try {
            setLoading(true);
            const templatesRef = ref(db, 'buildingChecks/templates');
            const snapshot = await get(templatesRef);

            if (snapshot.exists()) {
                const data = snapshot.val() as Record<string, BuildingCheckTemplate>;
                const templatesArray = Object.entries(data).map(([id, template]) => ({
                    ...template,
                    id
                }));
                setTemplates(templatesArray);
            } else {
                setTemplates([]);
            }
        } catch (error) {
            console.error('Error loading templates:', error);
            setAlert({ type: 'danger', message: 'Fel vid laddning av mallar' });
        } finally {
            setLoading(false);
        }
    };

    // Load condition assessment settings from Firebase
    const loadConditionAssessmentSettings = async (): Promise<void> => {
        try {
            const loadedSettings: Partial<ConditionAssessmentSettings> = {};

            // Load each category
            for (const category of CONDITION_ASSESSMENT_CATEGORIES) {
                const categoryKey = category.key as keyof ConditionAssessmentSettings;
                const categoryRef = ref(db, `maintenance/assessmentSettings/${categoryKey}`);
                const snapshot = await get(categoryRef);

                if (snapshot.exists()) {
                    loadedSettings[categoryKey] = snapshot.val();
                }
            }

            // Merge loaded settings with defaults
            setConditionAssessment({
                roofAndDrainage: loadedSettings.roofAndDrainage || DEFAULT_CONDITION_ASSESSMENT_SETTINGS.roofAndDrainage,
                facadeAndWindows: loadedSettings.facadeAndWindows || DEFAULT_CONDITION_ASSESSMENT_SETTINGS.facadeAndWindows,
                surfaces: loadedSettings.surfaces || DEFAULT_CONDITION_ASSESSMENT_SETTINGS.surfaces
            });
        } catch (error) {
            console.error('Error loading condition assessment settings:', error);
            setAlert({ type: 'danger', message: 'Fel vid laddning av skickbedömningsinställningar' });
        }
    };

    // Save condition assessment settings to Firebase
    const saveConditionAssessmentSettings = async (): Promise<void> => {
        try {
            setSaving(true);

            // Save each category
            for (const category of CONDITION_ASSESSMENT_CATEGORIES) {
                const categoryKey = category.key as keyof ConditionAssessmentSettings;
                const categoryRef = ref(db, `maintenance/assessmentSettings/${categoryKey}`);
                await set(categoryRef, conditionAssessment[categoryKey]);
            }

            setAlert({ type: 'success', message: 'Skickbedömningsinställningar sparade framgångsrikt!' });
        } catch (error) {
            console.error('Error saving condition assessment settings:', error);
            setAlert({ type: 'danger', message: 'Fel vid sparande av skickbedömningsinställningar' });
        } finally {
            setSaving(false);
        }
    };

    // Save template to Firebase
    const saveTemplate = async (templateData: BuildingCheckTemplateFormData): Promise<void> => {
        // Show validation errors if form is invalid
        if (!isFormValid()) {
            setShowValidation(true);
            setModalAlert({ type: 'danger', message: 'Vänligen fyll i alla obligatoriska fält.' });
            return;
        }

        try {
            setSaving(true);
            const now = new Date().toISOString();
            const templateId = editingTemplate?.id || `template_${Date.now()}`;
            
            const template: any = {
                id: templateId,
                name: templateData.name,
                description: templateData.description,
                estimatedTime: templateData.estimatedTime,
                instructionalAttachments: templateData.instructionalAttachments,
                checklist: templateData.checklist.filter(item => item.trim() !== ''),
                interval: templateData.interval,
                nextDueDateCalculation: templateData.nextDueDateCalculation,
                photoRequired: templateData.photoRequired,
                estimatedCost: templateData.estimatedCost,
                advanceNotice: templateData.advanceNotice,
                createdAt: editingTemplate?.createdAt || now,
                updatedAt: now
            };

            // Only add takesMeasurement if it's enabled
            if (templateData.takesMeasurement.enabled) {
                const takesMeasurement: any = {
                    metric: templateData.takesMeasurement.metric
                };
                
                // Only add acceptableRange if we have min or max values
                if (templateData.takesMeasurement.hasMinValue || templateData.takesMeasurement.hasMaxValue) {
                    const acceptableRange: any = {};
                    
                    if (templateData.takesMeasurement.hasMinValue) {
                        acceptableRange.min = templateData.takesMeasurement.minValue;
                    }
                    
                    if (templateData.takesMeasurement.hasMaxValue) {
                        acceptableRange.max = templateData.takesMeasurement.maxValue;
                    }
                    
                    takesMeasurement.acceptableRange = acceptableRange;
                }
                
                template.takesMeasurement = takesMeasurement;
            }

            const templateRef = ref(db, `buildingChecks/templates/${templateId}`);
            await set(templateRef, template);

            setAlert({ type: 'success', message: 'Mall sparad framgångsrikt!' });
            setModalAlert(null);
            setShowModal(false);
            setEditingTemplate(null);
            resetForm();
            loadTemplates();
        } catch (error) {
            console.error('Error saving template:', error);
            setAlert({ type: 'danger', message: 'Fel vid sparande av mall' });
        } finally {
            setSaving(false);
        }
    };

    // Delete template
    const deleteTemplate = async (template: BuildingCheckTemplate): Promise<void> => {
        try {
            setSaving(true);
            const templateRef = ref(db, `buildingChecks/templates/${template.id}`);
            await remove(templateRef);

            setAlert({ type: 'success', message: 'Mall borttagen framgångsrikt!' });
            setShowDeleteModal(false);
            setTemplateToDelete(null);
            loadTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            setAlert({ type: 'danger', message: 'Fel vid borttagning av mall' });
        } finally {
            setSaving(false);
        }
    };

    // Reset form
    const resetForm = (): void => {
        setFormData({
            name: '',
            description: '',
            estimatedTime: 0,
            takesMeasurement: {
                enabled: false,
                metric: 'mm',
                hasMinValue: false,
                hasMaxValue: false,
                minValue: 0,
                maxValue: 100
            },
            instructionalAttachments: {},
            checklist: [''],
            interval: {
                value: 1,
                unit: 'months'
            },
            nextDueDateCalculation: 'scheduled',
            photoRequired: false,
            estimatedCost: 0,
            advanceNotice: 30
        });
        setShowValidation(false);
        setModalAlert(null);
    };

    // Open modal for new template
    const openNewTemplateModal = (): void => {
        resetForm();
        setEditingTemplate(null);
        setShowModal(true);
    };

    // Open modal for editing template
    const openEditTemplateModal = (template: BuildingCheckTemplate): void => {
        setFormData({
            name: template.name,
            description: template.description,
            estimatedTime: template.estimatedTime,
            takesMeasurement: {
                enabled: !!template.takesMeasurement,
                metric: template.takesMeasurement?.metric || 'mm',
                hasMinValue: template.takesMeasurement?.acceptableRange?.min !== undefined,
                hasMaxValue: template.takesMeasurement?.acceptableRange?.max !== undefined,
                minValue: template.takesMeasurement?.acceptableRange?.min ?? 0,
                maxValue: template.takesMeasurement?.acceptableRange?.max ?? 100
            },
            instructionalAttachments: template.instructionalAttachments || {},
            checklist: template.checklist && template.checklist.length > 0 ? template.checklist : [''],
            interval: template.interval,
            nextDueDateCalculation: template.nextDueDateCalculation,
            photoRequired: template.photoRequired,
            estimatedCost: template.estimatedCost,
            advanceNotice: template.advanceNotice
        });
        setEditingTemplate(template);
        setShowModal(true);
    };

    // Add checklist item
    const addChecklistItem = (): void => {
        setFormData(prev => ({
            ...prev,
            checklist: [...prev.checklist, '']
        }));
    };

    // Remove checklist item
    const removeChecklistItem = (index: number): void => {
        setFormData(prev => ({
            ...prev,
            checklist: prev.checklist.filter((_, i) => i !== index)
        }));
    };

    // Helper function to sanitize filename for Firebase Realtime Database keys
    const sanitizeFileNameForDB = (filename: string): string => {
        return filename
            .replace(/[.#$/\[\]]/g, '_')  // Replace forbidden characters with underscore
            .replace(/[^a-zA-Z0-9_-]/g, '_')  // Replace any other non-alphanumeric chars except underscore and dash
            .replace(/_+/g, '_')  // Replace multiple underscores with single underscore
            .replace(/^_|_$/g, '');  // Remove leading/trailing underscores
    };

    // Attachment handling functions
    const handleFileUpload = async (file: File, templateId?: string): Promise<void> => {
        if (!auth.currentUser) {
            setModalAlert({ type: 'danger', message: 'Du måste vara inloggad för att ladda upp filer' });
            return;
        }

        try {
            setSaving(true);
            
            // Create a unique filename
            const timestamp = Date.now();
            const originalFileName = file.name;
            // Create a sanitized key for Firebase Realtime Database (no dots, #, $, /, [, ])
            const dbKey = `${timestamp}_${sanitizeFileNameForDB(originalFileName)}`;
            
            console.log('File upload:', { originalFileName, dbKey });
            
            // Storage path: buildingChecks/templates/{templateId}/instructionalAttachments/{fileName}
            const storagePath = `buildingChecks/templates/${templateId || 'temp'}/instructionalAttachments/${originalFileName}`;
            const fileRef = storageRef(storage, storagePath);
            
            // Upload file
            const uploadResult = await uploadBytes(fileRef, file);
            const downloadURL = await getDownloadURL(uploadResult.ref);
            
            // Create PhotoModel
            const photoModel: PhotoModel = {
                key: originalFileName, // Keep original filename for display
                author: auth.currentUser.uid,
                date: new Date().toISOString(),
                width: 0, // Will be updated if it's an image
                height: 0, // Will be updated if it's an image
                storagePath: storagePath
            };
            
            // Update form state
            setFormData(prev => ({
                ...prev,
                instructionalAttachments: {
                    ...prev.instructionalAttachments,
                    [dbKey]: photoModel
                }
            }));
            
        } catch (error) {
            console.error('Error uploading file:', error);
            setModalAlert({ type: 'danger', message: 'Fel vid uppladdning av fil' });
        } finally {
            setSaving(false);
        }
    };

    const removeAttachment = (fileName: string): void => {
        setFormData(prev => {
            const newAttachments = { ...prev.instructionalAttachments };
            delete newAttachments[fileName];
            return {
                ...prev,
                instructionalAttachments: newAttachments
            };
        });
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>, templateId?: string): void => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        
        const file = files[0];
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setModalAlert({ type: 'danger', message: 'Filen är för stor. Maximal storlek är 10MB' });
            return;
        }
        
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
        if (!allowedTypes.includes(file.type)) {
            setModalAlert({ type: 'danger', message: 'Filtypen stöds inte. Tillåtna format: JPEG, PNG, GIF, WebP, PDF, TXT' });
            return;
        }
        
        handleFileUpload(file, templateId);
        
        // Reset input
        event.target.value = '';
    };

    // Update checklist item
    const updateChecklistItem = (index: number, value: string): void => {
        setFormData(prev => ({
            ...prev,
            checklist: prev.checklist.map((item, i) => i === index ? value : item)
        }));
    };


    // Form validation
    const isFormValid = (): boolean => {
        return (
            formData.name.trim() !== '' &&
            formData.description.trim() !== '' &&
            formData.interval.value > 0
        );
    };

    // Check if a specific field is valid
    const isFieldValid = (fieldName: string): boolean => {
        switch (fieldName) {
            case 'name':
                return formData.name.trim() !== '';
            case 'description':
                return formData.description.trim() !== '';
            case 'interval':
                return formData.interval.value > 0;
            default:
                return true;
        }
    };

    // Check if a field should show validation (only after save attempt)
    const shouldShowValidation = (fieldName: string): boolean => {
        if (!showValidation) return false;
        return !isFieldValid(fieldName);
    };

    useEffect(() => {
        loadTemplates();
        loadConditionAssessmentSettings();
    }, []);

    if (loading) {
        return (
            <Container>
                <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>Hantera kontrollmallar - Molnfastigheter</title>
            </Helmet>
            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            <Row className="mb-4">
                <Col>
                    <h2>Byggnadskontroller</h2>
                    <p className="text-muted">Hantera kontrollmallar och skickbedömning</p>
                </Col>
            </Row>

            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'templates')}>
                <Row>
                    <Col>
                        <Nav variant="tabs" className="mb-4">
                            <Nav.Item>
                                <Nav.Link eventKey="templates">Kontrollmallar</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="condition-assessment">Skickbedömning</Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Col>
                </Row>

                <Tab.Content>
                    <Tab.Pane eventKey="templates">
                        <Row className="mb-4">
                            <Col>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h4>Kontrollmallar</h4>
                                        <p className="text-muted">Hantera mallar för byggnadskontroller</p>
                                    </div>
                                    <Button variant="primary" onClick={openNewTemplateModal}>
                                        <Plus size={20} className="me-2" />
                                        Ny mall
                                    </Button>
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            {templates.length === 0 ? (
                                <Col>
                                    <Card>
                                        <Card.Body className="text-center py-5">
                                            <h5>Inga mallar än</h5>
                                            <p className="text-muted">Skapa din första kontrollmall för att komma igång.</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ) : (
                                templates.map(template => (
                                    <Col key={template.id} lg={6} xl={4} className="mb-4">
                                        <Card className="template-card">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                    <h5 className="card-title">{template.name}</h5>
                                                    <div className="btn-group">
                                                        <Button 
                                                            variant="outline-secondary" 
                                                            size="sm" 
                                                            onClick={() => openEditTemplateModal(template)}
                                                            title="Redigera"
                                                        >
                                                            <Edit size={16} />
                                                        </Button>
                                                        <Button 
                                                            variant="outline-danger" 
                                                            size="sm" 
                                                            onClick={() => {
                                                                setTemplateToDelete(template);
                                                                setShowDeleteModal(true);
                                                            }}
                                                            title="Ta bort"
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </div>
                                                </div>
                                                
                                                <p className="card-text text-muted small mb-3">{template.description}</p>
                                                
                                                <div className="template-details">
                                                    <div className="detail-item">
                                                        <strong>Intervall:</strong> Var {template.interval.value} {INTERVAL_UNITS.find(u => u.value === template.interval.unit)?.label.toLowerCase()}
                                                    </div>
                                                    <div className="detail-item">
                                                        <strong>Beräknad arbetstid:</strong> {template.estimatedTime} min
                                                    </div>
                                                    <div className="detail-item">
                                                        <strong>Beräknad kostnad:</strong> {template.estimatedCost} kr
                                                    </div>
                                                    {template.takesMeasurement && (
                                                        <div className="detail-item">
                                                            <strong>Mätning:</strong> {template.takesMeasurement.metric}
                                                            {template.takesMeasurement.acceptableRange && (
                                                                <span>
                                                                    {template.takesMeasurement.acceptableRange.min !== undefined && template.takesMeasurement.acceptableRange.max !== undefined && (
                                                                        <span> ({template.takesMeasurement.acceptableRange.min} - {template.takesMeasurement.acceptableRange.max})</span>
                                                                    )}
                                                                    {template.takesMeasurement.acceptableRange.min !== undefined && template.takesMeasurement.acceptableRange.max === undefined && (
                                                                        <span> (min: {template.takesMeasurement.acceptableRange.min})</span>
                                                                    )}
                                                                    {template.takesMeasurement.acceptableRange.min === undefined && template.takesMeasurement.acceptableRange.max !== undefined && (
                                                                        <span> (max: {template.takesMeasurement.acceptableRange.max})</span>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="detail-item">
                                                        <strong>Förvarning:</strong> {template.advanceNotice} dagar
                                                    </div>
                                                    <div className="detail-item">
                                                        <strong>Foto krävs:</strong> {template.photoRequired ? 'Ja' : 'Nej'}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))
                            )}
                        </Row>
                    </Tab.Pane>

                    <Tab.Pane eventKey="condition-assessment">
                        <Row className="mb-4">
                            <Col>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h4>Skickbedömning</h4>
                                        <p className="text-muted">
                                            Konfigurera grundintervall för periodisk utvärdering av underhållsplanen för specifika komponentkategorier.
                                        </p>
                                    </div>
                                    <Button 
                                        variant="primary" 
                                        onClick={saveConditionAssessmentSettings}
                                        disabled={saving}
                                    >
                                        {saving ? 'Sparar...' : 'Spara inställningar'}
                                    </Button>
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            {CONDITION_ASSESSMENT_CATEGORIES.map((category) => {
                                const categoryKey = category.key as keyof ConditionAssessmentSettings;
                                const categoryData = conditionAssessment[categoryKey];

                                return (
                                    <Col key={category.key} lg={12} xl={4} className="mb-4">
                                        <Card>
                                            <Card.Body>
                                                <h5 className="mb-4">{category.label}</h5>
                                                
                                                <Form>
                                                    {/* Interval Controls */}
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Kontrollintervall</Form.Label>
                                                        <Row>
                                                            <Col xs={5}>
                                                                <Form.Control
                                                                    type="number"
                                                                    min="1"
                                                                    value={categoryData.interval.value}
                                                                    onChange={(e) => {
                                                                        const value = parseInt(e.target.value) || 1;
                                                                        setConditionAssessment(prev => ({
                                                                            ...prev,
                                                                            [categoryKey]: {
                                                                                ...prev[categoryKey],
                                                                                interval: {
                                                                                    ...prev[categoryKey].interval,
                                                                                    value
                                                                                }
                                                                            }
                                                                        }));
                                                                    }}
                                                                />
                                                            </Col>
                                                            <Col xs={7}>
                                                                <Form.Select
                                                                    value={categoryData.interval.unit}
                                                                    onChange={(e) => {
                                                                        const unit = e.target.value as 'days' | 'weeks' | 'months' | 'years';
                                                                        setConditionAssessment(prev => ({
                                                                            ...prev,
                                                                            [categoryKey]: {
                                                                                ...prev[categoryKey],
                                                                                interval: {
                                                                                    ...prev[categoryKey].interval,
                                                                                    unit
                                                                                }
                                                                            }
                                                                        }));
                                                                    }}
                                                                >
                                                                    {INTERVAL_UNITS.map(unit => (
                                                                        <option key={unit.value} value={unit.value}>
                                                                            {unit.label}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                            </Col>
                                                        </Row>
                                                    </Form.Group>

                                                    {/* Instruction */}
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Instruktion</Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={5}
                                                            value={categoryData.instruction}
                                                            onChange={(e) => {
                                                                setConditionAssessment(prev => ({
                                                                    ...prev,
                                                                    [categoryKey]: {
                                                                        ...prev[categoryKey],
                                                                        instruction: e.target.value
                                                                    }
                                                                }));
                                                            }}
                                                            placeholder="Skriv instruktioner för skickbedömning..."
                                                        />
                                                    </Form.Group>

                                                    {/* Summary */}
                                                    <div className="alert alert-info small mb-0">
                                                        <strong>Sammanfattning:</strong> Utvärdering var {categoryData.interval.value} {INTERVAL_UNITS.find(u => u.value === categoryData.interval.unit)?.label.toLowerCase()}
                                                    </div>
                                                </Form>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            {/* Template Modal */}
            <Modal show={showModal} onHide={() => { setShowModal(false); setModalAlert(null); }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingTemplate ? 'Redigera kontrollmall' : 'Ny kontrollmall'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalAlert && (
                        <Alert variant={modalAlert.type} className="mb-3">
                            {modalAlert.message}
                        </Alert>
                    )}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Namn *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="T.ex. Brandskyddskontroll"
                                isInvalid={shouldShowValidation('name')}
                                isValid={showValidation && isFieldValid('name') && formData.name.trim() !== ''}
                            />
                            {shouldShowValidation('name') && (
                                <Form.Control.Feedback type="invalid">
                                    Namn är obligatoriskt
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Beskrivning *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Skriv instruktion här."
                                isInvalid={shouldShowValidation('description')}
                                isValid={showValidation && isFieldValid('description') && formData.description.trim() !== ''}
                            />
                            {shouldShowValidation('description') && (
                                <Form.Control.Feedback type="invalid">
                                    Beskrivning är obligatorisk
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Beräknad arbetstid (minuter)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={formData.estimatedTime}
                                        onChange={(e) => setFormData(prev => ({ ...prev, estimatedTime: parseInt(e.target.value) || 0 }))}
                                        placeholder="0"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Beräknad kostnad (kr)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={formData.estimatedCost}
                                        onChange={(e) => setFormData(prev => ({ ...prev, estimatedCost: parseInt(e.target.value) || 0 }))}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Förvarning (dagar)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={formData.advanceNotice}
                                        onChange={(e) => setFormData(prev => ({ ...prev, advanceNotice: parseInt(e.target.value) || 0 }))}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Interval */}
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Intervall *</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            value={formData.interval.value}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                interval: { ...prev.interval, value: parseInt(e.target.value) || 1 }
                                            }))}
                                            style={{ width: '100px' }}
                                            isInvalid={shouldShowValidation('interval')}
                                            isValid={showValidation && isFieldValid('interval') && formData.interval.value > 0}
                                        />
                                        <Form.Select
                                            value={formData.interval.unit}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                interval: { ...prev.interval, unit: e.target.value as any }
                                            }))}
                                        >
                                            {INTERVAL_UNITS.map(unit => (
                                                <option key={unit.value} value={unit.value}>{unit.label}</option>
                                            ))}
                                        </Form.Select>
                                    </div>
                                    {shouldShowValidation('interval') && (
                                        <Form.Control.Feedback type="invalid">
                                            Intervall måste vara större än 0
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Nästa förfallodatum beräknas</Form.Label>
                            <Form.Select
                                value={formData.nextDueDateCalculation}
                                onChange={(e) => setFormData(prev => ({ ...prev, nextDueDateCalculation: e.target.value as any }))}
                            >
                                {NEXT_DUE_DATE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {/* Measurement */}
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Kontrollen tar mätvärde"
                                checked={formData.takesMeasurement.enabled}
                                onChange={(e) => setFormData(prev => ({ 
                                    ...prev, 
                                    takesMeasurement: { ...prev.takesMeasurement, enabled: e.target.checked }
                                }))}
                            />
                        </Form.Group>

                        {formData.takesMeasurement.enabled && (
                            <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Enhet</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={formData.takesMeasurement.metric}
                                                    onChange={(e) => setFormData(prev => ({ 
                                                        ...prev, 
                                                        takesMeasurement: { ...prev.takesMeasurement, metric: e.target.value }
                                                    }))}
                                                    placeholder="T.ex. mm, kg, liter, bar, °C"
                                                />
                                            </Form.Group>
                                        </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label="Har min-värde"
                                            checked={formData.takesMeasurement.hasMinValue}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                takesMeasurement: { ...prev.takesMeasurement, hasMinValue: e.target.checked }
                                            }))}
                                        />
                                        {formData.takesMeasurement.hasMinValue && (
                                            <Form.Control
                                                type="number"
                                                placeholder="Min värde"
                                                value={formData.takesMeasurement.minValue}
                                                onChange={(e) => setFormData(prev => ({ 
                                                    ...prev, 
                                                    takesMeasurement: { ...prev.takesMeasurement, minValue: parseFloat(e.target.value) || 0 }
                                                }))}
                                                className="mt-2"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label="Har max-värde"
                                            checked={formData.takesMeasurement.hasMaxValue}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                takesMeasurement: { ...prev.takesMeasurement, hasMaxValue: e.target.checked }
                                            }))}
                                        />
                                        {formData.takesMeasurement.hasMaxValue && (
                                            <Form.Control
                                                type="number"
                                                placeholder="Max värde"
                                                value={formData.takesMeasurement.maxValue}
                                                onChange={(e) => setFormData(prev => ({ 
                                                    ...prev, 
                                                    takesMeasurement: { ...prev.takesMeasurement, maxValue: parseFloat(e.target.value) || 0 }
                                                }))}
                                                className="mt-2"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {/* Attachments Section */}
                        <Form.Group className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Form.Label className="fw-medium">Instruktionsbilagor</Form.Label>
                                <label className="btn btn-outline-primary btn-sm mb-0">
                                    <Upload size={14} className="me-1" />
                                    Ladda upp fil
                                    <input
                                        type="file"
                                        className="d-none"
                                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt"
                                        onChange={(e) => handleFileInputChange(e, editingTemplate?.id)}
                                        disabled={saving}
                                    />
                                </label>
                            </div>

                            {Object.keys(formData.instructionalAttachments).length === 0 && (
                                <div className="text-muted fst-italic small mb-3">
                                    Inga bilagor bifogade ännu
                                </div>
                            )}

                            {Object.entries(formData.instructionalAttachments).map(([dbKey, attachment]) => (
                                <div key={dbKey} className="card bg-light border-0 mb-2">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <Image size={16} className="text-muted me-2" />
                                                <div>
                                                    <div className="fw-medium small">{attachment.key}</div>
                                                    <div className="text-muted small">
                                                        Uppladdad: {new Date(attachment.date).toLocaleDateString('sv-SE')}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeAttachment(dbKey)}
                                                className="btn btn-outline-danger btn-sm"
                                                type="button"
                                                disabled={saving}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {Object.keys(formData.instructionalAttachments).length > 0 && (
                                <div className="alert alert-info small mt-2 mb-0">
                                    <strong>Info:</strong> Bilagor lagras i Firebase Storage och är tillgängliga för alla som använder denna mall.
                                </div>
                            )}
                        </Form.Group>

                        {/* Photo requirement */}
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Foto av arbetet krävs"
                                checked={formData.photoRequired}
                                onChange={(e) => setFormData(prev => ({ ...prev, photoRequired: e.target.checked }))}
                            />
                        </Form.Group>

                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Avbryt
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => saveTemplate(formData)}
                        disabled={saving}
                    >
                        {saving ? 'Sparar...' : 'Spara mall'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta borttagning</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Är du säker på att du vill ta bort mallen <strong>{templateToDelete?.name}</strong>?</p>
                    <p className="text-muted">Denna åtgärd kan inte ångras.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Avbryt
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={() => templateToDelete && deleteTemplate(templateToDelete)}
                        disabled={saving}
                    >
                        {saving ? 'Tar bort...' : 'Ta bort'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default BuildingCheckTemplatesEditor;
